import React from 'react'
import { Teacher } from '../../providers/context'
import { ControlledModal } from '../../layouts/components/modal'
import { TextInput } from '../../components/form/textInput'
import { Select } from '../../components/form/select'
import { UserSelector } from '../../components/userSelector/userSelector'
import { Button } from '../../components/simple/button'
import { BasicUser } from '../../components/userSelector/users'
import { loadBasicUsersByIdentifiers } from '../../components/userSelector/userSelectorPlugins'
import { makeRequest, showAxiosErrorAsToast } from '../../request'
import { Popups } from '../../providers/popups'

export type TeacherGroup = {
  id: number
  name: string
  teachers: {
    id: number
    name: string
  }[]
  schoolID?: number
}

export const NEW_TEACHER_GROUP_STATE: EditTeacherGroupState = {
  group: undefined,
}
export type EditTeacherGroupState = { group: TeacherGroup | undefined }

export const EditTeacherGroupModal: React.FC<{
  editTeacherGroupState: EditTeacherGroupState
  setEditTeacherGroupState: React.Dispatch<React.SetStateAction<EditTeacherGroupState | undefined>>
  onSaved: () => void
  user: Teacher
}> = ({ editTeacherGroupState, setEditTeacherGroupState, onSaved, user }) => {
  const [loading, setLoading] = React.useState(false)

  const saveGroup = (id: number | undefined, school: number | 'district', name: string, teachers: number[]) => {
    setLoading(true)
    makeRequest('/teacherGroup/edit', {
      id,
      school,
      name,
      teachers,
    })
      .then(() => {
        onSaved()
        setEditTeacherGroupState(undefined)
      })
      .catch(showAxiosErrorAsToast)
      .finally(() => setLoading(false))
  }

  const [name, setName] = React.useState(editTeacherGroupState.group?.name || '')
  const [teachers, setTeachers] = React.useState<BasicUser[]>(editTeacherGroupState.group?.teachers || [])

  const schoolOptions = React.useMemo(
    () =>
      (user.districtAccess ? [{ id: 'district', name: 'District' }, ...user.district.schools] : user.schools).map(
        (s) => ({
          value: s.id as number | 'district',
          name: s.name,
        })
      ),
    [user]
  )
  const [school, setSchool] = React.useState(
    editTeacherGroupState.group
      ? schoolOptions.find((s) => s.value === editTeacherGroupState.group!.schoolID) || schoolOptions[0]
      : schoolOptions[0]
  )

  const recipientSearchData = React.useMemo(
    () => ({
      group: editTeacherGroupState.group ? editTeacherGroupState.group.id : 'new',
      school: school.value,
    }),
    [editTeacherGroupState.group, school]
  )

  return (
    <ControlledModal
      open={true}
      onOpenChange={() => {
        setEditTeacherGroupState(undefined)
      }}
      size="sm"
      disabled={loading}
      title={editTeacherGroupState.group ? 'Edit Teacher Group' : 'New Teacher Group'}
      className="gap-4"
    >
      <TextInput value={name} onChange={setName} placeholder="Group Title" />
      <Select value={school} onChange={setSchool} options={schoolOptions} />
      <UserSelector<BasicUser>
        selectedUsers={teachers}
        setSelectedUsers={setTeachers}
        fetchUsersByIdentifiersForPaste={loadBasicUsersByIdentifiers('teacher')}
        userDescriptor="teachers"
        url="/frontend_api/teacherGroupRecipients"
        searchData={recipientSearchData}
      />
      <div className="flex items-center gap-2">
        <Button
          logo
          onClick={() => {
            saveGroup(
              editTeacherGroupState.group?.id,
              school.value,
              name,
              teachers.map((t) => t.id)
            )
          }}
          disabled={name.trim().length === 0 || teachers.length < 2}
        >
          Save
        </Button>
        {editTeacherGroupState.group && (
          <Button
            onClick={async () => {
              if (!(await Popups.simpleConfirm('Are you sure you want to delete this group?'))) return
              saveGroup(editTeacherGroupState.group!.id, 0, 'deleted', [])
            }}
          >
            Delete
          </Button>
        )}
      </div>
    </ControlledModal>
  )
}
